import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { db } from '@vibe/db/server';
import { portalEvents, classifieds } from '@generated/models';
import { PortalOrdersBoard, EditorialInboxBoard, SubscriptionsBoard } from '@api/BoardSDK';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const shift = (day: string, n: number) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
// All report dates are New York days. Timestamps convert to NY; a bare date (midnight UTC) is kept as-is.
const nyDay = (v: Date | string | null | undefined) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  if (d.toISOString().endsWith('T00:00:00.000Z')) return d.toISOString().slice(0, 10);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
};
const VIEW_TYPES = ['visit', 'view_ads', 'view_classifieds', 'view_subscribe', 'view_merch', 'view_editorial', 'view_letters', 'view_jobs', 'ad_started', 'ad_completed', 'classified_completed', 'job_sales', 'job_freelance', 'job_phone', 'view_mail', 'mail_call', 'mail_email'] as const;
const MAX = 1000; // safety cap per section

/** Everything for the printable staff report between two dates (inclusive). */
export const getStaffReport = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), from: z.string().regex(DAY), to: z.string().regex(DAY) }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const { from, to } = data;

    // Count by New York date (older events were stamped with the UTC date, which rolls over at 8pm NY).
    const viewsP = (async () => {
      const tally = new Map<string, number>(VIEW_TYPES.map((t) => [t, 0]));
      let cursor: string | undefined;
      let pages = 0;
      do {
        const q = db.collection(portalEvents).where('day', '>=', from).where('day', '<=', shift(to, 1)).limit(1000);
        const page = await (cursor ? q.startAfter(cursor) : q).getPage();
        for (const d of page.docs) {
          const day = nyDay(d.data.createdAt);
          if (day >= from && day <= to && tally.has(d.data.type)) tally.set(d.data.type, (tally.get(d.data.type) ?? 0) + 1);
        }
        cursor = page.cursor;
      } while (cursor && ++pages < 50);
      return [...tally.entries()];
    })();

    const ordersP = (async () => {
      const board = new PortalOrdersBoard();
      const cols = ['status', 'orderDate', 'contactPerson', 'email', 'phone', 'publication', 'adSize', 'duration', 'startMonth', 'monthlyRate', 'totalContractValue', 'paymentMethod', 'specialInstructions', 'adFiles'] as const;
      let res = await board.items().withColumns([...cols]).where({ orderDate: { between: [shift(from, -1), shift(to, 1)] } }).orderBy({ column: 'orderDate', direction: 'asc' }).withPagination({ limit: 100 }).execute();
      const out = [...res.items];
      while (res.cursor && out.length < MAX) { res = await board.items().withColumns([...cols]).withPagination({ cursor: res.cursor }).execute(); out.push(...res.items); }
      return out.filter((o) => { const d = nyDay(o.orderDate); return d >= from && d <= to; });
    })();

    const editorialP = (async () => {
      const board = new EditorialInboxBoard();
      const cols = ['status', 'type', 'paper', 'message', 'senderName', 'email', 'phone', 'anonymous', 'received'] as const;
      let res = await board.items().withColumns([...cols]).where({ received: { between: [shift(from, -1), shift(to, 1)] } }).orderBy({ column: 'received', direction: 'asc' }).withPagination({ limit: 100 }).execute();
      const out = [...res.items];
      while (res.cursor && out.length < MAX) { res = await board.items().withColumns([...cols]).withPagination({ cursor: res.cursor }).execute(); out.push(...res.items); }
      // Tips are stamped in UTC; keep the ones whose New York date falls in the range.
      return out.filter((t) => { const d = nyDay(t.received); return d >= from && d <= to; });
    })();

    const classifiedsP = (async () => {
      const out: ({ id: string } & import('@generated/models').Classified)[] = [];
      let cursor: string | undefined;
      do {
        // Range filters on createdAt return nothing in Vibe DB, so walk newest-first and keep the dates in range.
        const q = db.collection(classifieds).orderBy('createdAt', 'desc').limit(100);
        const page = await (cursor ? q.startAfter(cursor) : q).getPage();
        let older = false;
        for (const d of page.docs) {
          const day = nyDay(d.data.createdAt);
          if (day < from) { older = true; break; }
          if (day && day <= to) out.push({ id: d.id, ...d.data });
        }
        if (older) break;
        cursor = page.cursor;
      } while (cursor && out.length < MAX);
      return out.reverse();
    })();

    const subsP = (async () => {
      // No date column on Subscriptions: walk newest-first by creation time, keep the New York dates in range.
      const board = new SubscriptionsBoard();
      let res = await board.items().withColumns(['status', 'email', 'phone', 'publication', 'startMonth', 'amount', 'paymentMethod', 'gift', 'giftRecipient']).orderBy({ column: 'createdAt', direction: 'desc' }).withPagination({ limit: 100 }).execute();
      const out: typeof res.items = [];
      for (;;) {
        let older = false;
        for (const s of res.items) { const d = nyDay(s.createdAt); if (d < from) { older = true; break; } if (d <= to) out.push(s); }
        if (older || !res.cursor || out.length >= MAX) break;
        res = await board.items().withColumns(['status', 'email', 'phone', 'publication', 'startMonth', 'amount', 'paymentMethod', 'gift', 'giftRecipient']).withPagination({ cursor: res.cursor }).execute();
      }
      return out.reverse();
    })();
    const [views, orders, editorial, cls, subs] = await Promise.all([viewsP, ordersP, editorialP, classifiedsP, subsP]);
    const isSponsor = (o: (typeof orders)[number]) => /brennan|video sponsorship|SPONSORSHIP INFO REQUESTED/i.test(`${o.publication ?? ''} ${o.adSize ?? ''} ${o.specialInstructions ?? ''}`);
    const isLetter = (e: (typeof editorial)[number]) => e.name.startsWith('Letter to the Editor');
    const isMug = (o: (typeof orders)[number]) => o.publication === 'Star-Revue Mug';

    return {
      from, to,
      views: Object.fromEntries(views) as Record<(typeof VIEW_TYPES)[number], number>,
      ads: orders.filter((o) => !/video only/i.test(o.publication ?? '') && !isMug(o) && o.publication !== 'Donation' && o.publication !== 'Payment link'),
      mugs: orders.filter(isMug),
      payments: orders.filter((o) => o.publication === 'Payment link').map((o) => { const m = /^Payment started(?: \((.+?)\))?: (.*)$/.exec(o.name); return { ...o, kind: m?.[1] ?? 'Pay link', who: m?.[2] ?? o.name }; }),
      donations: orders.filter((o) => o.publication === 'Donation'),
      subscriptions: subs,
      sponsorships: orders.filter(isSponsor).map((o) => ({ ...o, infoRequested: /SPONSORSHIP INFO REQUESTED/.test(o.specialInstructions ?? '') })),
      classifieds: cls,
      tips: editorial.filter((e) => !isLetter(e)),
      letters: editorial.filter(isLetter),
    };
  }));

/** Staff: remove a news tip from the Editorial Inbox (archived in monday, recoverable there). */
export const deleteTip = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), id: z.string().regex(/^\d+$/) }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    await new EditorialInboxBoard().item(data.id).archive().execute();
    return { ok: true };
  }));
